import Footer from '@/components/Footer';
import { ChangeExchangePriceFooterContent } from '@/components/manage/change-trips/ChangeExchangePriceFooterContent';
import TransferTime from '@/components/manage/tickets/TransferTime';
import OfferCard from '@/components/purchase/journey/OfferCard';
import ServiceTextMessage from '@/components/purchase/journey/ServiceTextMessage';
import StopDetails from '@/components/purchase/journey/StopDetails';
import { purchaseFlowBookingCreationLoadingSelector } from '@/features/loading/loadingSelectors';
import { useChangeBookingSelection } from '@/hooks/useChangeBookingSelection';
import { TransText } from '@/i18n/trans/text';
import { type RootState } from '@/store';
import { useSelector } from '@/store/utils';
import type {
  LegDTO,
  SearchJourneyOfferDTO,
  SearchJourneyOffersDTO,
} from '@/types/dto/agent';
import type { OfferMapByLegId, OfferMapItem } from '@/types/offer';
import { breakpoints } from '@/utils/breakpoints';
import { getDurationBetweenTimestamps } from '@/utils/dateTime';
import { isNonEmptyString } from '@/utils/typeGuards';
import { Button, Card, Icons, Typography } from '@fleet/webshop-ui';
import type { ActionCreatorWithPayload, Selector } from '@reduxjs/toolkit';
import { type FC, Fragment, type ReactNode, useCallback, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';

interface JourneyOfferListProps {
  selectedJourney?: SearchJourneyOffersDTO;
  updateSelectedOfferMap: ActionCreatorWithPayload<OfferMapItem>;
  selectedOfferMapByLegIdSelector: Selector<RootState, OfferMapByLegId>;
  title: ReactNode;
  onContinue: () => void;
  onGoBack: () => void;
}

const JourneyOfferList: FC<JourneyOfferListProps> = ({
  selectedJourney,
  updateSelectedOfferMap,
  selectedOfferMapByLegIdSelector,
  title,
  onContinue,
  onGoBack,
}) => {
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const isPending = useSelector(purchaseFlowBookingCreationLoadingSelector);
  const { hasSelection } = useChangeBookingSelection();
  const legIds = useMemo(
    () =>
      selectedJourney?.trips?.reduce<Array<string>>((acc, { legs }) => {
        acc.push(...(legs ?? []).map((leg) => leg.id).filter(isNonEmptyString));

        return acc;
      }, []),
    [selectedJourney?.trips]
  );

  const getTripLegPart = useCallback(
    (legId?: string) => {
      if (!legId || !legIds || !legIds.length) return;

      return `${legIds.indexOf(legId) + 1}/${legIds.length}`;
    },
    [legIds]
  );

  const getOffersOrderedByPriceByLeg = useCallback(
    (offers: Array<SearchJourneyOfferDTO> | undefined, leg: LegDTO) => {
      if (!offers?.length) {
        return [];
      }

      return offers
        .filter(
          ({ coveredLegIds }) => leg.id && coveredLegIds?.includes(leg.id)
        )
        .sort((a, b) => (a.price?.amount ?? 0) - (b.price?.amount ?? 0));
    },
    []
  );

  const getOffersColumns = useCallback(
    (offers: Array<SearchJourneyOfferDTO>) => {
      const firstColumn: Array<SearchJourneyOfferDTO> = [];
      const secondColumn: Array<SearchJourneyOfferDTO> = [];

      offers.forEach((offer, index) => {
        if (index % 2 === 0) {
          firstColumn.push(offer);
        } else {
          secondColumn.push(offer);
        }
      });

      return [firstColumn, secondColumn];
    },
    []
  );

  const renderOfferCards = useCallback(
    (offers: Array<SearchJourneyOfferDTO>, leg: LegDTO): ReactNode => {
      if (!offers?.length) {
        return null;
      }

      return offers.map((offer, i) => (
        <OfferCard
          key={`${offer.id}-${i}`}
          offer={offer}
          legId={leg.id}
          journeyDetails={{
            originStopName: leg.originStop?.name,
            destinationStopName: leg.destinationStop?.name,
          }}
          updateSelectedOfferMap={updateSelectedOfferMap}
          selectedOfferMapByLegIdSelector={selectedOfferMapByLegIdSelector}
        />
      ));
    },
    [selectedOfferMapByLegIdSelector, updateSelectedOfferMap]
  );

  return (
    <>
      <div className="mx-auto flex w-full max-w-laptop flex-col p-0 pb-24 laptop:p-4 laptop:pb-24">
        <Typography
          variant={isLaptopOrBigger ? 'heading1' : 'heading2'}
          className="p-4 laptop:p-0 laptop:pb-4"
          as="h1"
        >
          {title}
        </Typography>
        <Card
          className="rounded-none p-0 laptop:rounded-lg"
          data-testid="journey-offer-list"
        >
          {selectedJourney?.trips?.map(({ offers, legs }) =>
            legs?.map((leg, key) => {
              const nextLeg = legs[key + 1];
              const transferTime = nextLeg
                ? getDurationBetweenTimestamps(
                    leg.arrivalTime,
                    nextLeg.departureTime
                  )
                : null;
              const serviceTexts = (leg.serviceAttributes ?? []).filter(
                ({ code }) => code === 'SERVICE_TEXT'
              );

              const orderedOffers = getOffersOrderedByPriceByLeg(offers, leg);
              const [firstColumn, secondColumn] =
                getOffersColumns(orderedOffers);

              return (
                <Fragment key={key}>
                  <div className="flex flex-col gap-6 p-4 laptop:flex-row laptop:items-start">
                    <div className="flex flex-col gap-4 laptop:w-[328px]">
                      <div className="laptop:flex-shrink-0">
                        {legIds?.length && legIds.length > 1 && (
                          <Typography
                            variant="body1-bold"
                            className="mb-1 pl-1.5 text-primary"
                            as="h2"
                          >
                            <TransText
                              i18nKey="tripLegPartIndex"
                              values={{ tripLegPart: getTripLegPart(leg.id) }}
                            />
                          </Typography>
                        )}
                        <StopDetails leg={leg} />
                      </div>
                      <ServiceTextMessage serviceTexts={serviceTexts} />
                    </div>
                    {isLaptopOrBigger ? (
                      <div className="flex flex-1 flex-row gap-3">
                        <div className="flex w-full flex-col gap-3">
                          {renderOfferCards(firstColumn, leg)}
                        </div>
                        <div className="flex w-full flex-col gap-3">
                          {renderOfferCards(secondColumn, leg)}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-4">
                        {renderOfferCards(orderedOffers, leg)}
                      </div>
                    )}
                  </div>
                  {Boolean(transferTime) && (
                    <TransferTime formattedTime={transferTime} />
                  )}
                </Fragment>
              );
            })
          )}
        </Card>
      </div>
      <Footer
        content={
          hasSelection ? <ChangeExchangePriceFooterContent /> : undefined
        }
        actionButtons={
          <>
            <Button
              size={isLaptopOrBigger ? 'medium' : 'large'}
              data-testid="footer-back-button"
              className="rounded-lg laptop:w-auto"
              variant="tertiary"
              onClick={onGoBack}
              startAdornment={<Icons.arrowLeft />}
            >
              <TransText i18nKey="back" />
            </Button>
            <Button
              size={isLaptopOrBigger ? 'medium' : 'large'}
              data-testid="footer-continue-button"
              className="rounded-lg laptop:w-auto"
              variant="cta"
              onClick={onContinue}
              isPending={isPending}
              endAdornment={<Icons.arrowRight />}
            >
              <TransText i18nKey="continue" />
            </Button>
          </>
        }
      />
    </>
  );
};

export default JourneyOfferList;
